// import { MarkerClusterer } from "@googlemaps/markerclusterer";
import {GlobalVariables} from "../GlobalVariables";

export default class MapMaker {

	static createDynamicMap(element) {
		const map = new google.maps.Map(element, {
			zoom: 10,
			clickableIcons: false,
			streetViewControl: false,
			mapTypeControl: false,
			fullscreenControl: false,
			zoomControl: false,
			gestureHandling: "cooperative",
			mapId: mcArgs.gmapMapId
		});

		let isCtrlOrCmdPressed = false;

		// Surveille les touches Cmd / Ctrl
		window.addEventListener('keydown', (e) => {
			if (e.metaKey || e.ctrlKey) {
				isCtrlOrCmdPressed = true;
			}
		});

		window.addEventListener('keyup', (e) => {
			if (!e.metaKey && !e.ctrlKey) {
				isCtrlOrCmdPressed = false;
				if (GlobalVariables?.lenis) GlobalVariables.lenis.start();
			}
		});

		// Gère le comportement de Lenis au survol
		element.addEventListener('mouseenter', () => {
			if (isCtrlOrCmdPressed && GlobalVariables?.lenis) {
				GlobalVariables.lenis.stop();
			}
		});

		element.addEventListener('mouseleave', () => {
			if (GlobalVariables?.lenis) {
				GlobalVariables.lenis.start();
			}
		});

		// Met à jour dynamiquement si l’utilisateur appuie sur Cmd/Ctrl pendant qu’il est déjà sur la map
		element.addEventListener('wheel', () => {
			if (GlobalVariables?.lenis) {
				if (isCtrlOrCmdPressed) {
					GlobalVariables.lenis.stop();
				} else {
					GlobalVariables.lenis.start();
				}
			}
		});

		map.bounds = new google.maps.LatLngBounds();
		map.markers = [];

		return map;
	}

    static createStaticMap(element, markers) {

        let imgWidth = "640";
        let imgHeight = "640";
        if (element.classList.contains('map-fullwidth')) {
            imgHeight = "360";
        }

        let url = "https://maps.googleapis.com/maps/api/staticmap?";
        url += "key=" + mcArgs.gmapApiKey;
        url += "&map_id=" + mcArgs.gmapMapId;
        url += "&size=" + imgWidth + "x" + imgHeight;
        url += "&scale=2";
        url += "&format=JPEG";

        if (markers.length === 1) {
            url += "&center=" + markers[0].dataset.markerLat + "," + markers[0].dataset.markerLng;
            url += "&zoom=" + element.dataset.zoom;
        }

        markers.forEach((marker) => {
            url += "&markers=";
            url += marker.dataset.markerLat + "," + marker.dataset.markerLng;
            if (marker.dataset.pointer) {
                url += "|icon:" + encodeURI(marker.dataset.pointer);
            }
        });

        let image = new Image();
        image.src = url;
        image.width = imgWidth;
        image.height = imgHeight;
        element.appendChild(image);

    }

    static zoomControls(element, map) {
        let zoomIn = element.closest('.map-container').querySelector('.map-zoom-in');
        if (zoomIn) {
            zoomIn.addEventListener('click', () => {
                map.setZoom(map.getZoom() + 1);
            });
        }

        let zoomOut = element.closest('.map-container').querySelector('.map-zoom-out');
        if (zoomOut) {
            zoomOut.addEventListener('click', () => {
                map.setZoom(map.getZoom() - 1);
            });
        }
    }

    static createMarker(marker, map) {
        let m = new google.maps.marker.AdvancedMarkerElement({
            map,
            position: {
                lat: parseFloat(marker.dataset.markerLat),
                lng: parseFloat(marker.dataset.markerLng)
            },
            gmpClickable: true
        });

        // Icône personnalisée
        let glyphImg = null;
        if (marker.dataset.pointer) {
            glyphImg = document.createElement('img');
            glyphImg.src = marker.dataset.pointer;
            let s = marker.dataset.pointerSize.split('x');
            glyphImg.style.width = s[0] + "px";
            glyphImg.style.height = s[1] + "px";

            m.content = glyphImg;
        }

        // Contenu de l'InfoWindow
        const title = marker.dataset.title ?? "";
        const image = marker.dataset.image ?? "";
        const address = MapMaker.formatAddress(marker.dataset.address ?? "");
        const contentHTML = `
		    <div class="map-tooltip">
		      <div class="map-tooltip-title label-bigger">${title}</div>
		      <div class="map-tooltip-content">
		        ${image ? `<img src="${image}" class="map-tooltip-logo" alt="">` : ""}
		        ${address ? `<p class="map-tooltip-address">${address}</p>` : ""}
		      </div>
		    </div>
		  `;

        // Ouvre l’InfoWindow au clic
        const info = MapMaker.getInfoWindow(map);
        m.addListener('gmp-click', () => {
            MapMaker.zoomToMarker(map, m, 11, () => {
                info.setContent(contentHTML);
                info.open({anchor: m, map});
            });
        });

        // Ferme l'InfoWindow si on clic ailleurs
        map.addListener("click", () => {
            const info = MapMaker.getInfoWindow(map);
            info.close();
        });

        map.markers.push(m);
        map.bounds.extend(m.position);
    }

    static createMarkerClusterer(map, markers) {
        map.markerClusterer = new markerClusterer.MarkerClusterer({
            markers,
            map,
            renderer: {
                render: ({count, position}) => {
                    const size = count >= 100 ? 64 : count >= 25 ? 52 : 40;
                    return new google.maps.marker.AdvancedMarkerElement({
                        map,
                        position,
                        content: this.creatSVGIcon(size, String(count)),
                        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                        gmpClickable: true
                    });
                }
            },
            onClusterClick: (event, cluster /*, mapInstance */) => {
                // Calcule des bounds du cluster
                const items = cluster && (cluster.markers || (typeof cluster.getMarkers === 'function' ? cluster.getMarkers() : [])) || [];
                const bounds = new google.maps.LatLngBounds();
                items.forEach(m => bounds.extend(m.position));

                // Fit animé puis limite de zoom max (ex: 14)
                MapMaker.fitBoundsWithMaxZoom(map, bounds, 11);
            }
        });
        return map.markerClusterer;
    }

    static fitBoundsWithMaxZoom(map, bounds, maxZoom = 14) {
        if (!bounds) return;
        map.fitBounds(bounds);
        google.maps.event.addListenerOnce(map, 'idle', () => {
            const z = map.getZoom();
            if (Number.isFinite(z) && Number.isFinite(maxZoom) && z > maxZoom) {
                map.setZoom(maxZoom);
            }
        });
    }

    static refreshClusterer(map, markers) {
        const mc = map && map.markerClusterer;
        if (!mc) return;

        if (typeof mc.clearMarkers === 'function') {
            mc.clearMarkers();
        } else if (typeof mc.setMarkers === 'function') {
            mc.setMarkers([]);
        }

        if (typeof mc.addMarkers === 'function') {
            mc.addMarkers(markers || []);
        } else if (typeof mc.setMarkers === 'function') {
            mc.setMarkers(markers || []);
        }

        if (typeof mc.repaint === 'function') {
            mc.repaint();
        } else if (typeof mc.render === 'function') {
            mc.render();
        }
    }

    static updateBounds(map, markers = null) {
        const list = (markers && markers.length) ? markers : map.markers;
        if (!list || !list.length) return;

        const bounds = new google.maps.LatLngBounds();
        list.forEach((m) => bounds.extend(m.position));

        if (list.length > 1) {
            map.fitBounds(bounds);
        } else {
            map.setCenter(list[0].position);
        }
    }

    static creatSVGIcon(size = 40, text = "1") {
        const r = size / 2;
        const fontSize = Math.round(size * 0.35);
        const stroke = Math.max(2, Math.round(size * 0.06)); // liseré blanc optionnel

        const svg = `
		    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
		      <defs>
		        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
		          <stop offset="0%" style="stop-color:#3F1A72;stop-opacity:1" />
		          <stop offset="100%" style="stop-color:#4E4BA6;stop-opacity:1" />
		        </linearGradient>
		      </defs>
		      <circle cx="${r}" cy="${r}" r="${r}" fill="url(#grad)" stroke="rgba(255,255,255,0.2)" stroke-width="${stroke}" />
		      <text x="50%" y="54%" text-anchor="middle" dominant-baseline="middle"
		            font-family="NeueMachinaInktrap, Arial, sans-serif" font-size="${fontSize}" font-weight="600"
		            fill="#fff">${text}</text>
		    </svg>`;

        const div = document.createElement('div');
        div.style.width = `${size}px`;
        div.style.height = `${size}px`;
        div.style.lineHeight = '0';
        div.innerHTML = svg.trim();
        div.title = `${text} société${text === "1" ? "" : "s"}`; // accessibilité/tooltip
        return div;
    }

    // Adresse Google "Rue, 26700 Ville, France" -> une ligne par segment, code postal + ville en capitales, pays France masqué
    static formatAddress(address) {
        const escape = (str) => str.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
        const lines = address.split(",").map((line) => line.trim()).filter(Boolean);

        if (lines.length > 1 && lines[lines.length - 1].toLowerCase() === "france") {
            lines.pop();
        }

        return lines
            .map((line) => escape(/^\d{4,5}\s/.test(line) ? line.toUpperCase() : line))
            .join("<br>");
    }

    static getInfoWindow(map) {
        if (!map._infoWindow) {
            map._infoWindow = new google.maps.InfoWindow({headerDisabled: true});
        }
        return map._infoWindow;
    }

    static zoomToMarker(map, marker, targetZoom = 12, after = null) {
        const pos = marker && marker.position ? marker.position : marker;

        // 1) Pan animé
        map.panTo(pos);

        // 2) Zoom seulement si nécessaire (aussi animé)
        const needZoom = Number.isFinite(targetZoom) && map.getZoom() !== targetZoom;
        if (needZoom) {
            google.maps.event.addListenerOnce(map, 'idle', () => {
                map.setZoom(targetZoom);
                google.maps.event.addListenerOnce(map, 'idle', () => {
                    after && after();
                });
            });
        } else {
            google.maps.event.addListenerOnce(map, 'idle', () => {
                after && after();
            });
        }
    }
}
